import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoader from "../../components/pageLoader";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import {
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import DeleteConfirmationModal from "../../components/deleteModal";
import {
  createShortenLink,
  deleteShortenLink,
  getShortenLinks,
  updateShortenLink,
  updateShortenLinkStatus,
} from "../../api/shortenLinksApi";
import ShortLinksFormModal from "./shortLinksFormModal";
import StatusModal from "./statusModal";

const ShortLinksPage = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const [deleteLinkId, setDeleteLinkId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);

  const [statusModal, setStatusModal] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [saving, setSaving] = useState(false);

  const LIMIT = 10;

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [copied, setCopied] = useState(null);

  const totalPages = Math.ceil(total / LIMIT);

  const fetchShortenLinks = async () => {
    try {
      setLoading(true);

      const offset = (page - 1) * LIMIT;

      const data = await getShortenLinks(LIMIT, offset);
      const list = Array.isArray(data) ? data : data.results || [];
      setTotal(data.count || 0);
      setLinks(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async () => {
    try {
      setDeleting(true);
      await deleteShortenLink(deleteLinkId);

      setDeleteLinkId(null);

      // Refresh companies list
      const offset = (page - 1) * LIMIT;
      const data = await getShortenLinks(LIMIT, offset);
      setLinks(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleShortenLinkSubmit = async (values) => {
    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", values.name);
      formData.append("need_qr_code", values.need_qr_code);
      formData.append("original_url", values.link);

      await createShortenLink(formData);

      setShowAddModal(false);

      // Refresh list
      const offset = (page - 1) * LIMIT;
      const data = await getShortenLinks(LIMIT, offset);
      setLinks(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async ({ id }) => {
    try {
      setUpdatingStatus(true);

      await updateShortenLinkStatus(id);

      fetchShortenLinks();

      setStatusModal(null);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCopy = async (text, type, id) => {
    if (!text) return;

    await navigator.clipboard.writeText(text);

    setCopied(`${type}-${id}`);

    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownload = async (url, name = "qr-code") => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${name}.png`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  useEffect(() => {
    fetchShortenLinks();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">
            Shorten Links & QRs
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Create & Manage Shorten Links / QR Codes
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium transition"
        >
          + Create
        </button>
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-slate-400">
            <tr>
              <th className="px-6 py-3 text-center">Name</th>
              <th className="px-6 py-3 text-center">Created at/ by</th>
              <th className="px-6 py-3 text-center">Link</th>
              <th className="px-6 py-3 text-center">Short Link</th>
              <th className="px-6 py-3 text-center">Click Count</th>
              <th className="px-6 py-3 text-center">QR Code</th>
              <th className="px-6 py-3 text-center">Status</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {links.map((lk) => (
              <tr
                key={lk.id}
                className="border-b text-center border-white/5 hover:bg-slate-800/40 transition cursor-pointer"
              >
                <td className="px-6 py-4 text-slate-300">{lk.name || "-"}</td>
                <td className="px-6 py-4 text-slate-300">
                  {lk.created_at || "-"}
                </td>

                <td className="px-6 py-4 text-slate-300">
                  <a
                    href={lk.original_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-pink-500 hover:underline"
                  >
                    View
                  </a>
                </td>
                <td className="py-2">
                  {lk.short_url ? (
                    <div className="flex items-center justify-center gap-3">
                      <a
                        href={lk.short_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-pink-500 hover:underline"
                      >
                        View
                      </a>
                      |
                      <button
                        onClick={() => handleCopy(lk.short_url, "link", lk.id)}
                        className={`transition ${
                          copied === `page-${lk.id}`
                            ? "text-green-400"
                            : "text-slate-400 hover:text-white"
                        }`}
                        title="Copy link"
                      >
                        <ClipboardIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  {lk.click_count || "-"}
                </td>
                <td className="py-2">
                  {lk.qr_code ? (
                    <div className="flex items-center justify-center gap-3">
                      <a
                        href={lk.qr_code}
                        target="_blank"
                        rel="noreferrer"
                        className="text-pink-500 hover:underline"
                      >
                        View
                      </a>
                      |
                      <button
                        onClick={() =>
                          handleDownload(lk.qr_code, `qr-${lk.id}`)
                        }
                        className={`transition ${
                          copied === `short-${lk.id}`
                            ? "text-green-400"
                            : "text-slate-400 hover:text-white"
                        }`}
                        title="Download QR"
                      >
                        <DownloadIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 text-slate-300">
                  <span
                    className={lk.is_permanent ? "text-gray-400" : lk.is_active ? "text-green-400" : "text-red-400"}
                  >
                    {lk.is_permanent ? "Permanent" : lk.is_active ? "Active" : "Inactive" || "-"}
                  </span>
                </td>

                <td className="py-2 text-center space-x-3">
                  {lk.is_permanent ? (
                    <div>-</div>
                  ) : (
                    <div className="py-2 text-center space-x-3">
                      <button
                        onClick={() => {
                          setStatusModal(lk);
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        <PencilIcon className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteLinkId(lk.id);
                        }}
                        className="text-red-400 hover:text-red-500"
                      >
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}

            {!links.length && (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-10 text-center text-slate-400"
                >
                  No Links or QR Codes found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-end">
          {totalPages > 1 && (
            <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
              {/* Previous */}
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Previous
              </button>

              {/* Pages */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
                ${
                  page === pageNumber
                    ? "bg-pink-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                    >
                      {pageNumber}
                    </button>
                  ),
                )}
              </div>

              {/* Next */}
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-slate-800 text-white"
          }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <ShortLinksFormModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
        }}
        onSubmit={handleShortenLinkSubmit}
        loading={saving}
      />

      <StatusModal
        isOpen={!!statusModal}
        link={statusModal}
        onClose={() => setStatusModal(null)}
        onSubmit={handleStatus}
        loading={updatingStatus}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteLinkId}
        description="Are you sure you want to delete this link and QR?  This action cannot be undone."
        onClose={() => setDeleteLinkId(null)}
        onConfirm={handleDeleteLink}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default ShortLinksPage;
